import React, {
  ReactNode,
  ReactElement,
  forwardRef,
  RefObject,
  Component,
  createRef,
  FocusEvent,
  KeyboardEvent,
  MouseEvent,
  Ref,
} from "react";
import classNames from "classnames";
import { observable, action } from "mobx";
import { observer } from "mobx-react";
import { Transition, TransitionGroup } from "react-transition-group";
import { TransitionStatus } from "react-transition-group/Transition";
import ReactResizeDetector from "react-resize-detector/build/withPolyfill";
import { AnimationDuration } from "../../../styles/Animations";
import forceReflow from "../../../utils/forceReflow";
import { Warning, IconSize } from "../../icons";
import { randomString } from "../../../utils";
import { formatTelephone } from "../../../utils/formatString";

enum TextInputType {
  Text = "text",
  Telephone = "tel",
  Email = "email",
  Password = "password",
  Number = "number",
  Search = "search",
}

enum TextInputVariant {
  Bordered = "bordered",
  Underlined = "underlined",
  Borderless = "borderless",
}

enum TextInputSize {
  XSmall = "x-small",
  Small = "small",
  Medium = "medium",
  Large = "large",
}

enum TextInputLabelSize {
  Small = "small",
  Medium = "medium",
}

enum TextInputTheme {
  Dark = "dark",
  Light = "light",
}

interface ITextInputProps {
  /**
   * Changes from using an <input /> to a <textarea /> to support multiple lines
   */
  multiline?: boolean;
  /**
   * A succinct label for the input
   */
  label: string;
  /**
   * Hides the label visually, but keeps it available for assistive technology to read
   */
  labelHidden?: boolean;
  /**
   * Placeholders are an optional way to give an extra hint. Don't rely on placeholders
   * to convey essential information — use the label instead
   */
  placeholder?: string;
  /**
   * Light or dark color theme
   */
  theme?: TextInputTheme;
  /**
   * Stylistic input variant. Use Underlined for a more subtle appearance within other components
   */
  variant?: TextInputVariant;
  /**
   * A message to notify users of an input validation error and how to fix it
   */
  error?: ReactNode;
  /**
   * Size for the text input element. Changes the height, padding, and font-size
   */
  size?: TextInputSize;
  /**
   * Size for the label element
   */
  labelSize?: TextInputLabelSize;
  /**
   * A unique ID for the input. If not provided, one is generated automatically
   */
  id?: string;
  /**
   * An element to place at the start within the input, such as an icon or button
   */
  startElement?: ReactElement | null;
  /**
   * An element to place at the end within the input, such as an icon or button
   */
  endElement?: ReactElement | null;
  value?: string;
  onChange?: (value: string) => void;
  type?: TextInputType;
  className?: string;
  disabled?: boolean;
  onMouseEnter?: (event: MouseEvent<HTMLElement>) => void;
  onMouseLeave?: (event: MouseEvent<HTMLElement>) => void;
  onFocus?: (event: FocusEvent<HTMLElement>) => void;
  onBlur?: (event: FocusEvent<HTMLElement>) => void;
  onKeyDown?: (event: KeyboardEvent<HTMLElement>) => void;
  onClick?: (event: MouseEvent<HTMLElement>) => void;
  readOnly?: boolean;
  maxLength?: number;
  role?: string;
  elementSize?: number;
  autoComplete?: string;
}

interface IInnerTextInputProps extends ITextInputProps {
  innerRef?: Ref<any>;
}

const componentName = "text-input";

@observer
class TextInput extends Component<IInnerTextInputProps> {
  public static defaultProps = {
    type: TextInputType.Text,
    placeholder: "",
    variant: TextInputVariant.Bordered,
    size: TextInputSize.Medium,
    theme: TextInputTheme.Light,
    labelSize: TextInputLabelSize.Small,
  };

  private baseId = this.props.id || `${componentName}-${randomString()}`;
  private errorRef: RefObject<HTMLDivElement> = createRef();
  private endElementRef = createRef<HTMLDivElement>();
  private startElementRef = createRef<HTMLDivElement>();

  @observable.ref
  private isFocused = false;

  @observable.ref
  private startElementSize: number | undefined = undefined;

  @observable.ref
  private endElementSize: number | undefined = undefined;

  public componentDidMount(): void {
    this.measureElements();
  }

  public render(): ReactNode {
    const {
      type,
      multiline,
      label,
      placeholder,
      value,
      variant,
      className,
      labelHidden,
      size,
      theme,
      innerRef,
      error,
      labelSize,
      disabled,
      onMouseEnter,
      onMouseLeave,
      onKeyDown,
      onClick,
      maxLength,
      startElement,
      endElement,
      readOnly,
      role,
      onChange,
      id,
      elementSize,
      ...rest
    } = this.props;
    const inputId = `${this.baseId}-input`;
    const labelId = `${this.baseId}-label`;
    const errorId = `${this.baseId}-error`;
    const elementType = multiline ? "textarea" : "input";
    const InputElement = elementType;
    const isUnderlined = variant === TextInputVariant.Underlined;

    return (
      <div
        className={classNames(componentName, `${componentName}--${theme}`, className, {
          [`${componentName}--disabled`]: disabled,
        })}
      >
        <label
          className={classNames(`${componentName}__label`, `${componentName}__label--${labelSize}`, {
            [`${componentName}__label--hidden`]: labelHidden || isUnderlined,
          })}
          id={labelId}
          htmlFor={inputId}
        >
          {label}
        </label>
        <div className={`${componentName}__input-container`}>
          <InputElement
            className={classNames(
              `${componentName}__${elementType}`,
              `${componentName}__${elementType}--${variant}`,
              `${componentName}__${elementType}--${size}`,
              {
                [`${componentName}__${elementType}--error`]: error,
                [`${componentName}__${elementType}--has-start-element`]: startElement,
                [`${componentName}__${elementType}--has-end-element`]: endElement,
              },
            )}
            type={multiline ? undefined : type}
            ref={innerRef}
            id={inputId}
            aria-labelledby={labelId}
            aria-describedby={error ? errorId : undefined}
            aria-multiline={multiline}
            aria-invalid={!!error}
            placeholder={placeholder}
            onChange={this.onChange}
            value={value}
            onFocus={this.handleFocus}
            onBlur={this.handleBlur}
            disabled={disabled}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
            onKeyDown={onKeyDown}
            onClick={onClick}
            maxLength={maxLength}
            readOnly={readOnly}
            style={{ paddingInlineStart: this.startElementSize, paddingInlineEnd: this.endElementSize }}
            role={role}
            size={elementSize}
            {...rest}
          />
          {isUnderlined && (
            <Transition onEnter={forceReflow} in={this.isFocused} timeout={AnimationDuration.Medium}>
              {(status): ReactElement => (
                <div
                  className={classNames(`${componentName}__underline`, `${componentName}__underline--${status}`, {
                    [`${componentName}__underline--error`]: error,
                  })}
                />
              )}
            </Transition>
          )}
          {!!startElement && (
            <div ref={this.startElementRef} className={`${componentName}__element ${componentName}__element--start`}>
              <ReactResizeDetector handleWidth onResize={this.measureElements}>
                {startElement}
              </ReactResizeDetector>
            </div>
          )}
          {!!endElement && (
            <div ref={this.endElementRef} className={`${componentName}__element ${componentName}__element--end`}>
              <ReactResizeDetector handleWidth onResize={this.measureElements}>
                {endElement}
              </ReactResizeDetector>
            </div>
          )}
        </div>
        <TransitionGroup component={null}>
          {!!error && (
            <Transition onEnter={forceReflow} timeout={AnimationDuration.Short}>
              {(status): ReactElement => (
                <div
                  className={classNames(`${componentName}__error`, `${componentName}__error--${status}`)}
                  style={{ height: this.getErrorHeight(status) }}
                  id={errorId}
                  aria-live="polite"
                >
                  <div className={`${componentName}__error-content`} ref={this.errorRef}>
                    <div className={`${componentName}__error-icon`}>
                      <Warning size={IconSize.Small} />
                    </div>
                    <div className={`${componentName}__error-text`}>{error}</div>
                  </div>
                </div>
              )}
            </Transition>
          )}
        </TransitionGroup>
      </div>
    );
  }

  private onChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
    if (this.props.type == TextInputType.Telephone) {
      event.target.value = formatTelephone(event.target.value);
    }

    this.props.onChange?.(event.target.value);
  };

  @action
  private measureElements = () => {
    const { startElement, endElement } = this.props;

    const getWidth = (ref: RefObject<HTMLDivElement>): number | undefined => ref.current?.getBoundingClientRect().width;

    this.startElementSize = startElement ? getWidth(this.startElementRef) : undefined;
    this.endElementSize = endElement ? getWidth(this.endElementRef) : undefined;
  };

  @action
  private handleBlur = (event: FocusEvent<HTMLElement>): void => {
    this.isFocused = false;
    this.props.onBlur?.(event);
  };

  @action
  private handleFocus = (event: FocusEvent<HTMLElement>): void => {
    this.isFocused = true;
    this.props.onFocus?.(event);
  };

  private getErrorHeight = (status: TransitionStatus): number => {
    if (["entering", "entered"].includes(status)) {
      return this.errorRef.current?.getBoundingClientRect().height || 0;
    }
    return 0;
  };
}

export default forwardRef<HTMLElement, ITextInputProps>(function textInput(
  props: ITextInputProps,
  ref: Ref<HTMLElement>,
) {
  return <TextInput innerRef={ref} {...props} />;
});

export { TextInputType, ITextInputProps, TextInputVariant, TextInputSize, TextInputTheme, TextInputLabelSize };
